import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { UserCommunication } from '@this+that/models';

import { useShortcutsContext } from './ShortcutsContext';
import { ShortcutContext, ShortcutKey } from './types';
import { useKeyboardShortcuts } from './useKeyboardShortcuts';
import {
  useArchiveUserCommunication,
  useBulkDeleteDrafts,
  useMarkAsReadUserCommunication,
  useMarkAsUnreadUserCommunication,
} from '../UserCommunications/';
import { useCustomToast } from '../useCustomToast';

interface UseInboxDraftActionsProps {
  filteredCommunications: UserCommunication[];
  selectedItems: Set<string>;
  toggleItemSelection: (messageId: string) => void;
  clearSelection?: () => void;
}

export function useInboxDraftActions({
  filteredCommunications,
  selectedItems,
  toggleItemSelection,
  clearSelection,
}: UseInboxDraftActionsProps) {
  const { registerShortcut, unregisterShortcut, currentContext } =
    useKeyboardShortcuts();
  const { selectedEmailIndex } = useShortcutsContext();
  const { t } = useTranslation();
  const { promiseToast } = useCustomToast();

  const { handleArchiveUserCommunication } = useArchiveUserCommunication();
  const { handleMarkAsReadUserCommunication } =
    useMarkAsReadUserCommunication();
  const { handleMarkAsUnreadUserCommunication } =
    useMarkAsUnreadUserCommunication();
  const { handleBulkDeleteDrafts } = useBulkDeleteDrafts();

  useEffect(() => {
    const ctx = [ShortcutContext.DRAFT];
    unregisterShortcut(ShortcutKey.SELECT, ctx);
    unregisterShortcut(ShortcutKey.ARCHIVE, ctx);
    unregisterShortcut(ShortcutKey.MARK_AS_READ, ctx);
    unregisterShortcut(ShortcutKey.MARK_AS_UNREAD, ctx);
    unregisterShortcut(ShortcutKey.DELETE, ctx);
    unregisterShortcut(ShortcutKey.DELETE_KEY, ctx);
    unregisterShortcut(ShortcutKey.BACKSPACE, ctx);

    registerShortcut({
      key: ShortcutKey.SELECT,
      contexts: [ShortcutContext.DRAFT],
      handler: () => {
        if (
          selectedEmailIndex >= 0 &&
          selectedEmailIndex < filteredCommunications.length
        ) {
          const selectedEmail = filteredCommunications[selectedEmailIndex];
          if (selectedEmail) {
            toggleItemSelection(selectedEmail.messageId);
          }
        }
      },
      description: 'Select/deselect current email',
    });

    const archiveHandler = () => {
      if (selectedItems.size > 0) {
        const itemsArray = Array.from(selectedItems);
        clearSelection?.();

        promiseToast(
          handleArchiveUserCommunication(itemsArray),
          t('inbox.userCommunication.actions.archiving'),
          t('inbox.userCommunication.actions.archiveMultipleSuccess', {
            count: itemsArray.length,
          }),
          t('inbox.userCommunication.actions.archiveError'),
        );
      } else if (
        selectedEmailIndex >= 0 &&
        selectedEmailIndex < filteredCommunications.length
      ) {
        const selectedEmail = filteredCommunications[selectedEmailIndex];
        if (selectedEmail) {
          handleArchiveUserCommunication([selectedEmail.messageId]);
        }
      }
    };

    registerShortcut({
      key: ShortcutKey.ARCHIVE,
      contexts: [ShortcutContext.DRAFT],
      handler: archiveHandler,
      description: 'Archive selected draft(s)',
    });

    const markAsReadHandler = () => {
      if (selectedItems.size > 0) {
        const itemsArray = Array.from(selectedItems);

        promiseToast(
          handleMarkAsReadUserCommunication(itemsArray, clearSelection),
          t('inbox.userCommunication.actions.markingAsRead'),
          t('inbox.userCommunication.actions.markAsReadMultipleSuccess', {
            count: selectedItems.size,
          }),
          t('inbox.userCommunication.actions.markAsReadError'),
        );
      } else if (
        selectedEmailIndex >= 0 &&
        selectedEmailIndex < filteredCommunications.length
      ) {
        const selectedEmail = filteredCommunications[selectedEmailIndex];
        if (selectedEmail) {
          handleMarkAsReadUserCommunication([selectedEmail.messageId]);
        }
      }
    };

    registerShortcut({
      key: ShortcutKey.MARK_AS_READ,
      contexts: [ShortcutContext.DRAFT],
      handler: markAsReadHandler,
      description: 'Mark selected draft(s) as read',
    });

    const markAsUnreadHandler = () => {
      if (selectedItems.size > 0) {
        const itemsArray = Array.from(selectedItems);
        promiseToast(
          handleMarkAsUnreadUserCommunication(itemsArray, clearSelection),
          t('inbox.userCommunication.actions.markingAsUnread'),
          t('inbox.userCommunication.actions.markAsUnreadMultipleSuccess', {
            count: selectedItems.size,
          }),
          t('inbox.userCommunication.actions.markAsUnreadError'),
        );
      } else if (
        selectedEmailIndex >= 0 &&
        selectedEmailIndex < filteredCommunications.length
      ) {
        const selectedEmail = filteredCommunications[selectedEmailIndex];
        if (selectedEmail) {
          handleMarkAsUnreadUserCommunication([selectedEmail.messageId]);
        }
      }
    };

    registerShortcut({
      key: ShortcutKey.MARK_AS_UNREAD,
      contexts: [ShortcutContext.DRAFT],
      handler: markAsUnreadHandler,
      description: 'Mark selected draft(s) as unread',
      shiftKey: true,
    });

    const deleteHandler = () => {
      if (selectedItems.size > 0) {
        const itemsArray = Array.from(selectedItems);
        clearSelection?.();

        void handleBulkDeleteDrafts(itemsArray);
      } else if (
        selectedEmailIndex >= 0 &&
        selectedEmailIndex < filteredCommunications.length
      ) {
        const selectedEmail = filteredCommunications[selectedEmailIndex];
        if (selectedEmail) {
          handleBulkDeleteDrafts([selectedEmail.messageId]);
        }
      }
    };

    registerShortcut({
      key: ShortcutKey.DELETE,
      contexts: [ShortcutContext.DRAFT],
      handler: deleteHandler,
      description: 'Delete selected draft(s)',
    });

    registerShortcut({
      key: ShortcutKey.DELETE_KEY,
      contexts: [ShortcutContext.DRAFT],
      handler: deleteHandler,
      description: 'Delete selected draft(s)',
    });

    registerShortcut({
      key: ShortcutKey.BACKSPACE,
      contexts: [ShortcutContext.DRAFT],
      handler: deleteHandler,
      description: 'Delete selected draft(s)',
    });

    return () => {
      unregisterShortcut(ShortcutKey.SELECT, ctx);
      unregisterShortcut(ShortcutKey.ARCHIVE, ctx);
      unregisterShortcut(ShortcutKey.MARK_AS_READ, ctx);
      unregisterShortcut(ShortcutKey.MARK_AS_UNREAD, ctx);
      unregisterShortcut(ShortcutKey.DELETE, ctx);
      unregisterShortcut(ShortcutKey.DELETE_KEY, ctx);
      unregisterShortcut(ShortcutKey.BACKSPACE, ctx);
    };
  }, [
    registerShortcut,
    unregisterShortcut,
    selectedEmailIndex,
    filteredCommunications,
    selectedItems,
    toggleItemSelection,
    clearSelection,
    handleArchiveUserCommunication,
    handleMarkAsReadUserCommunication,
    handleMarkAsUnreadUserCommunication,
    handleBulkDeleteDrafts,
    currentContext,
  ]);
}
